import { useCallback, useState } from 'react';

import {
  CopyIcon,
  Trash2Icon,
  EllipsisVerticalIcon,
  CloudUploadIcon,
  DownloadIcon,
  SquarePenIcon,
  ArrowRightLeftIcon,
  EyeIcon,
  FolderInputIcon,
  FolderOutputIcon,
  MessagesSquareIcon,
  FilePenLineIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { MODAL_DOCUMENT_PREVIEW } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useDatasources from '@/hooks/useDatasources';
import { useAPIDraftDocuments } from '@/modules/collections/hooks/useAPIDraftDocuments';
import { openDeleteDocumentModal } from '@/redux/collections/actions';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setDatasourceId, setDocumentId } from '@/redux/session/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { trackEvent } from '@/segment/segment';
import { CollectionDocument, DatasourceType } from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import {
  DOCUMENT_TYPES_WITH_PREVIEW,
  ROOT_DATASOURCE_NAMES,
  ROOT_DATASOURCE_TYPES,
} from '../../../constants';
import { appendEllipsis, downloadDocument } from '../../../helper';
import { useTabCategory } from '../../../hooks/useCategory';
import { MoveToFolder } from '../MoveToFolder';

interface ActionsCellProps {
  item: CollectionDocument;
  moveDocument: (data: {
    document_id: string;
    new_datasource_id: string;
    datasource_id: string;
  }) => void;
  createDatasourceAsync: (data: {
    type: DatasourceType;
    name: string;
    config: Record<string, unknown>;
  }) => Promise<{ datasource_id: string }>;
}

const ActionsCell = ({ item, moveDocument, createDatasourceAsync }: ActionsCellProps) => {
  const { collectionId } = useParams();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isFAQTab, isDocumentsTab, isExternalTab } = useTabCategory();
  const showEditOption = isFAQTab || isDocumentsTab || isExternalTab;
  const showDownloadOption = isFAQTab || isDocumentsTab;
  const showReplaceOption = isFAQTab || isDocumentsTab;
  const showPreviewOption = isDocumentsTab && DOCUMENT_TYPES_WITH_PREVIEW.includes(item.type);
  const { datasources: allDatasources } = useDatasources(collectionId);
  const { publishDocuments, draftDocuments } = useAPIDraftDocuments({
    collection_id: collectionId,
  });

  const targetDatasources = allDatasources?.filter(
    (ds) =>
      ((isFAQTab && ds.type === DatasourceType.FAQ && ds.name !== ROOT_DATASOURCE_NAMES.faq) ||
        (isDocumentsTab &&
          ds.type === DatasourceType.FILES &&
          ds.name !== ROOT_DATASOURCE_NAMES.documents)) &&
      ds.datasource_id !== item.datasource_id
  );
  const [isSelectingFolder, setIsSelectingFolder] = useState(false);
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.DELETE)
  );
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.READ)
  );
  const slug = useSelector(selectAccountSlug);

  const handleEdit = useCallback(() => {
    if (isFAQTab) {
      dispatch(setSidebarContent('add_faq'));
    } else if (isDocumentsTab) {
      if (item.type === 'txt') {
        dispatch(setSidebarContent('add_txt_document'));
      } else {
        dispatch(setSidebarContent('edit_document'));
      }
    } else if (isExternalTab) {
      dispatch(setSidebarContent('add_external_sources'));
    }
    dispatch(setDocumentId(item.document_id));
  }, [dispatch, isDocumentsTab, isExternalTab, isFAQTab, item.document_id, item.type]);

  const handleCopyId = useCallback(async () => {
    trackEvent(EventName.ClickCopyDocumentId, { document_id: item.document_id });

    try {
      await navigator.clipboard.writeText(item.document_id);
      toast.success(t('common.copied'), { duration: 1000 });
    } catch {
      toast.error(t('collections.error_copying_id'), { duration: 4000 });
    }
  }, [item, t]);

  const handlePublish = useCallback(async () => {
    await publishDocuments({ document_ids: [item.document_id], datasource_id: item.datasource_id });
  }, [item.document_id, item.datasource_id, publishDocuments]);

  const handleDraft = useCallback(async () => {
    await draftDocuments({ document_ids: [item.document_id], datasource_id: item.datasource_id });
  }, [item.document_id, item.datasource_id, draftDocuments]);

  const handleDownload = useCallback(() => {
    downloadDocument(item, collectionId, slug, isFAQTab ? 'csv' : undefined);
  }, [item, slug, collectionId, isFAQTab]);

  const handleReplace = useCallback(() => {
    const { document_id, datasource_id } = item;

    dispatch(setDocumentId(document_id));
    dispatch(setDatasourceId(datasource_id));
    if (isFAQTab) {
      dispatch(setSidebarContent('upload_faq'));
    } else if (isDocumentsTab) {
      dispatch(setSidebarContent('upload_documents'));
    }
  }, [dispatch, item, isFAQTab, isDocumentsTab]);

  const handlePreview = useCallback(() => {
    const { document_id, datasource_id } = item;

    dispatch(
      pushModal(MODAL_DOCUMENT_PREVIEW, { document_id, datasource_id, collection_id: collectionId })
    );
  }, [dispatch, item, collectionId]);

  const handleDelete = useCallback(() => {
    dispatch(
      openDeleteDocumentModal({ datasource_id: item.datasource_id, document_id: item.document_id })
    );
  }, [dispatch, item.document_id, item.datasource_id]);

  const handleRemoveFromFolder = useCallback(async () => {
    const rootDatasourceName =
      ROOT_DATASOURCE_TYPES[isDocumentsTab ? DatasourceType.FILES : DatasourceType.FAQ].name;

    let rootDatasourceId = allDatasources?.find(
      (ds) => ds.name === rootDatasourceName
    )?.datasource_id;

    if (!rootDatasourceId) {
      const rootDatasource = await createDatasourceAsync(
        ROOT_DATASOURCE_TYPES[isDocumentsTab ? DatasourceType.FILES : DatasourceType.FAQ]
      );
      rootDatasourceId = rootDatasource.datasource_id;
    }
    moveDocument({
      document_id: item.document_id,
      new_datasource_id: rootDatasourceId,
      datasource_id: item.datasource_id,
    });
  }, [
    allDatasources,
    createDatasourceAsync,
    isDocumentsTab,
    item.datasource_id,
    item.document_id,
    moveDocument,
  ]);

  const handleConvertToFAQ = useCallback(async () => {
    const rootFAQDatasourceName = ROOT_DATASOURCE_TYPES[DatasourceType.FAQ].name;

    let rootFAQDatasourceId = allDatasources?.find(
      (ds) => ds.name === rootFAQDatasourceName
    )?.datasource_id;

    if (!rootFAQDatasourceId) {
      const rootFAQDatasource = await createDatasourceAsync(
        ROOT_DATASOURCE_TYPES[DatasourceType.FAQ]
      );
      rootFAQDatasourceId = rootFAQDatasource.datasource_id;
    }

    moveDocument({
      document_id: item.document_id,
      new_datasource_id: rootFAQDatasourceId,
      datasource_id: item.datasource_id,
    });

    toast.success(t('collections.document_converted_to_faq'), {
      description: t('collections.document_converted_to_faq_description'),
    });
  }, [
    allDatasources,
    createDatasourceAsync,
    item.datasource_id,
    item.document_id,
    moveDocument,
    t,
  ]);

  const handleMoveToFolder = (e: Event) => {
    // Prevent menu from closing when selecting folder
    e.preventDefault();
    setIsSelectingFolder(true);
  };

  const handleMoveToFolderSubmit = async (
    data: { datasource_id: string },
    event?: React.BaseSyntheticEvent
  ) => {
    if (event) {
      event.preventDefault();
    }
    moveDocument({
      document_id: item.document_id,
      new_datasource_id: data.datasource_id,
      datasource_id: item.datasource_id,
    });
    setIsSelectingFolder(false);
  };

  const showRemoveFromFolderOption = isDocumentsTab || isFAQTab;
  const showMoveToFolderOption = isDocumentsTab || isFAQTab;
  const showConvertToFAQOption = isDocumentsTab && item.type === 'txt';

  return (
    <DropdownMenu
      modal={false}
      onOpenChange={(open) => {
        if (!open) {
          setIsSelectingFolder(false);
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          onClick={(e) => {
            // Prevent from navigating to the collection page
            e.preventDefault();
            e.stopPropagation();
          }}
          variant="ghost-neutral"
          size="icon-sm"
        >
          <EllipsisVerticalIcon size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => {
          // Prevent handleRowClick from being executed
          e.stopPropagation();
        }}
      >
        {isSelectingFolder ? (
          <MoveToFolder datasources={targetDatasources} onSubmit={handleMoveToFolderSubmit} />
        ) : (
          <>
            {/* Conditional options */}
            {showEditOption && (
              <DropdownMenuItem
                onSelect={handleEdit}
                disabled={!canWrite}
                className="flex items-center gap-2"
              >
                <SquarePenIcon size={16} className="text-foreground-muted" />
                {appendEllipsis(t('common.edit'))}
              </DropdownMenuItem>
            )}
            {showDownloadOption && (
              <DropdownMenuItem
                onSelect={handleDownload}
                disabled={!canRead}
                className="flex items-center gap-2"
              >
                <DownloadIcon size={16} className="text-foreground-muted" />
                {t('collections.download')}
              </DropdownMenuItem>
            )}
            {showReplaceOption && (
              <DropdownMenuItem
                onSelect={handleReplace}
                disabled={!canWrite}
                className="flex items-center gap-2"
              >
                <ArrowRightLeftIcon size={16} className="text-foreground-muted" />
                {appendEllipsis(t('collections.replace'))}
              </DropdownMenuItem>
            )}
            {showPreviewOption && (
              <DropdownMenuItem
                onSelect={handlePreview}
                disabled={!canRead}
                className="flex items-center gap-2"
              >
                <EyeIcon size={16} className="text-foreground-muted" />
                {t('common.preview')}
              </DropdownMenuItem>
            )}
            {showRemoveFromFolderOption && (
              <DropdownMenuItem
                onSelect={handleRemoveFromFolder}
                disabled={!canWrite}
                className="flex items-center gap-2"
              >
                <FolderOutputIcon size={16} className="text-foreground-muted" />
                {t('collections.remove_from_folder')}
              </DropdownMenuItem>
            )}

            {showMoveToFolderOption && targetDatasources?.length > 0 && (
              <DropdownMenuItem
                onSelect={handleMoveToFolder}
                disabled={!canWrite}
                className="flex items-center gap-2"
              >
                <FolderInputIcon size={16} className="text-foreground-muted" />
                {appendEllipsis(t('collections.move_to_folder'))}
              </DropdownMenuItem>
            )}
            {showConvertToFAQOption && (
              <DropdownMenuItem
                onSelect={handleConvertToFAQ}
                disabled={!canWrite}
                className="flex items-center gap-2"
              >
                <MessagesSquareIcon size={16} className="text-foreground-muted" />
                {t('collections.convert_to_faq')}
              </DropdownMenuItem>
            )}
            {/* Common options */}
            {!item.is_draft ? (
              <DropdownMenuItem
                onSelect={handleDraft}
                disabled={!canWrite}
                className="flex items-center gap-2"
              >
                <FilePenLineIcon size={16} className="text-foreground-muted" />
                {t('collections.mark_as_draft')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onSelect={handlePublish}
                disabled={!canWrite}
                className="flex items-center gap-2"
              >
                <CloudUploadIcon size={16} className="text-foreground-muted" />
                {t('collections.publish')}
              </DropdownMenuItem>
            )}

            <DropdownMenuItem onSelect={handleCopyId} className="flex items-center gap-2">
              <CopyIcon size={16} className="text-foreground-muted" />
              {t('collections.datasources.copy_id')}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!canDelete}
              variant="destructive"
              className="flex items-center gap-2"
              onSelect={handleDelete}
            >
              <Trash2Icon size={16} />
              {appendEllipsis(t('common.delete'))}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ActionsCell;
